import { useEffect } from 'react';
import type { MouseEvent } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { useTranslation } from '@/i18n';
import type { Key } from '@/i18n';
import { cx } from '@/lib/cx';
import type { Health, IntegrationSourceInfo } from './api';
import { healthTone } from './parts';
import { SourcePanel } from './SourcePanel';
import { useMonitorParams } from './useMonitorParams';
import { useSources } from './useMonitor';
import styles from './MonitorPage.module.css';

/**
 * The source screen: every feed the application can run — delivered or not — its
 * settings, and its traffic light. Broken feeds sort to the top, because a monitor
 * is read for what is wrong, not alphabetically.
 */
export const SourcesView = () => {
  const { params, select } = useMonitorParams();
  const { sources, isLoading, errorKey, reload, apply } = useSources();
  const { t } = useTranslation();

  /**
   * While an archive re-read runs, the list refreshes itself so `replayProgress`
   * advances without the reader hammering the button. Only then: `health` going
   * stale by a minute is not worth a request every 30 seconds forever.
   */
  const replaying = sources.some((source) => source.replayPending);

  useEffect(() => {
    if (!replaying) return undefined;

    const timer = window.setInterval(reload, 30_000);
    return () => window.clearInterval(timer);
  }, [replaying, reload]);

  const ordered = [...sources].sort(bySeverity);
  const selected = params.panel
    ? (sources.find((source) => source.sourceCode === params.panel) ?? null)
    : null;

  return (
    <>
      <div className={styles.viewBar}>
        <p className={styles.note}>{t('monitor.sources.lead')}</p>
        <Button size="sm" onClick={reload} disabled={isLoading}>
          {t('common.refresh')}
        </Button>
      </div>

      {errorKey && (
        <p className={styles.error} role="alert">
          {t(errorKey)}
        </p>
      )}

      <div className={styles.tableWrap}>
        {isLoading && <div className={styles.loadingBar} aria-hidden="true" />}

        <table className={cx(styles.table, isLoading && styles.loading)}>
          <thead>
            <tr>
              <th>{t('monitor.sources.columns.source')}</th>
              <th>{t('monitor.sources.columns.health')}</th>
              <th>{t('monitor.sources.columns.interval')}</th>
              <th>{t('monitor.sources.columns.trust')}</th>
              <th>{t('monitor.sources.columns.updated')}</th>
            </tr>
          </thead>

          <tbody>
            {ordered.length === 0 && !isLoading ? (
              <tr>
                <td colSpan={5} className={styles.message}>
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              ordered.map((source) => (
                <SourceRow
                  key={source.sourceCode}
                  source={source}
                  isOpen={selected?.sourceCode === source.sourceCode}
                  onOpen={() => select(source.sourceCode)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {selected !== null && (
        <SourcePanel
          key={selected.sourceCode}
          source={selected}
          onSaved={apply}
          onClose={() => select(null)}
        />
      )}
    </>
  );
};

/**
 * Red first, then amber, then green, then grey — and disabled sources last of all,
 * since nothing is expected of them. Ties break by code so the order is stable.
 */
const SEVERITY: Record<Health, number> = { FAILING: 0, STALE: 1, OK: 2, UNKNOWN: 3 };

const bySeverity = (left: IntegrationSourceInfo, right: IntegrationSourceInfo): number => {
  const rank = (source: IntegrationSourceInfo) =>
    (source.enabled ? 0 : 10) + SEVERITY[source.health];

  return rank(left) - rank(right) || left.sourceCode.localeCompare(right.sourceCode);
};

const SourceRow = ({
  source,
  isOpen,
  onOpen,
}: {
  source: IntegrationSourceInfo;
  isOpen: boolean;
  onOpen: () => void;
}) => {
  const { t, formatDateTime } = useTranslation();

  const handleRowClick = (event: MouseEvent<HTMLTableRowElement>) => {
    // Same rule as every clickable row here: selecting text to copy is not a click.
    if (event.detail > 0 && (window.getSelection()?.toString() ?? '') !== '') return;
    onOpen();
  };

  return (
    <tr
      className={cx(styles.row, isOpen && styles.selected, !source.enabled && styles.mutedRow)}
      onClick={handleRowClick}
    >
      <td>
        <button type="button" className={styles.codeButton} aria-haspopup="dialog">
          {source.sourceCode}
        </button>
        {source.displayName && <span className={styles.subLine}>{source.displayName}</span>}
        {source.replayPending && (
          <span className={styles.subLine}>
            {source.replayProgress
              ? t('monitor.sources.replaying', { progress: source.replayProgress })
              : t('monitor.sources.replayingStart')}
          </span>
        )}
      </td>

      <td>
        {source.enabled ? (
          <Badge tone={healthTone[source.health]}>{t(`monitor.health.${source.health}`)}</Badge>
        ) : (
          // A disabled source is not expected to deliver, so its light would be a
          // verdict on nothing — "off" is the honest state.
          <Badge tone="danger">{t('monitor.sources.off')}</Badge>
        )}
      </td>

      <td className={styles.numCell}>{intervalLabel(source.pollIntervalSeconds, t)}</td>

      {/* 50 is the default that means "not ranked" — only a real ranking is worth ink. */}
      <td className={styles.numCell}>
        {source.trustRank === 50 ? <span className={styles.empty}>50</span> : source.trustRank}
      </td>

      <td className={styles.subCell}>
        {source.configured
          ? `${source.updatedBy ?? ''} ${source.updated ? formatDateTime(source.updated) : ''}`.trim() ||
            '—'
          : t('monitor.sources.defaults')}
      </td>
    </tr>
  );
};

/** `30 s`, `15 min` — or "push" where nothing is polled and staleness is not judged. */
const intervalLabel = (
  seconds: number | null,
  t: (key: Key, values?: Record<string, string | number>) => string,
): string => {
  if (seconds === null) return t('monitor.sources.push');
  if (seconds >= 120 && seconds % 60 === 0) return t('monitor.sources.minutes', { count: seconds / 60 });
  return t('monitor.sources.seconds', { count: seconds });
};
